/**
 * The PRIME nested-diamond mark, as numbers.
 *
 * This module is the single source of truth for the mark's proportions. The
 * inline SVG (`PrimeDiamondMark.jsx`) and the three.js summon
 * (`primeSummoningScene.js`) are both built from it, so the animation cannot
 * drift away from the logo it resolves into — the last frame of the summon and
 * the resting mark are the same shape by construction, not by eye.
 *
 * Kept free of React and of three.js so it can be unit tested on its own.
 *
 * ── Where the numbers come from ──────────────────────────────────────────────
 * Read off the "PRIME identity — nested diamond" design doc's primary lockup,
 * which draws the mark in a 104px box as three `rotate(45deg)` squares:
 *
 *   outer   74 x 74   1.75px border   rgba(255,255,255,0.35)
 *   inner   45 x 45   1.75px border   #ffffff
 *   core    19 x 19   filled          #7fb3d5
 *
 * A 74px square rotated 45 degrees measures 74 x sqrt(2) = 104.65px corner to
 * corner, so the outer diamond exactly circumscribes its box. That makes the
 * outer half-diagonal the natural unit: everything below is expressed with the
 * outer diamond spanning -1..+1 on both axes, which is also the coordinate
 * space the 3D scene works in.
 */

/** Design-doc pixel sizes, kept so the derivation stays checkable. */
export const SOURCE_SPEC = Object.freeze({
  box: 104,
  outerSquare: 74,
  innerSquare: 45,
  coreSquare: 19,
  strokePx: 1.75,
});

const SQRT2 = Math.SQRT2;
/** Half-diagonal of the outer square, in design-doc pixels. */
const UNIT = (SOURCE_SPEC.outerSquare * SQRT2) / 2;

/** Convert a design-doc square edge length into a normalised half-diagonal. */
function halfDiagonal(squarePx) {
  return (squarePx * SQRT2) / 2 / UNIT;
}

export const MARK_COLORS = Object.freeze({
  /** On the navy sign-in hero. */
  dark: Object.freeze({
    outer: 'rgba(255,255,255,0.35)',
    inner: '#ffffff',
    core: '#7fb3d5',
  }),
  /** The doc's one-colour-on-light variant. */
  light: Object.freeze({
    outer: 'rgba(13,35,56,0.35)',
    inner: '#0d2338',
    core: '#0d2338',
  }),
});

/**
 * The three rings, outermost first.
 *
 * `r` is the half-diagonal in outer-diamond units. `stroke` is the stroke width
 * in those same units; a filled ring has `stroke: 0`.
 */
export const MARK_RINGS = Object.freeze([
  Object.freeze({
    id: 'outer',
    r: halfDiagonal(SOURCE_SPEC.outerSquare),
    stroke: SOURCE_SPEC.strokePx / UNIT,
    filled: false,
  }),
  Object.freeze({
    id: 'inner',
    r: halfDiagonal(SOURCE_SPEC.innerSquare),
    stroke: SOURCE_SPEC.strokePx / UNIT,
    filled: false,
  }),
  Object.freeze({
    id: 'core',
    r: halfDiagonal(SOURCE_SPEC.coreSquare),
    stroke: 0,
    filled: true,
  }),
]);

/** The mark is square: the outer diamond spans the full box on both axes. */
export const MARK_ASPECT = 1;

/**
 * Half-extent of the drawn mark, outer stroke included.
 *
 * The outer stroke straddles r = 1, so the art actually reaches 1 + stroke/2.
 * Both renderers frame to this number — the SVG uses it as its `viewBox`, and
 * the 3D scene solves its resting camera distance from it — which is what makes
 * the summon's last frame and the resting SVG the same size on screen, so the
 * cross-fade between them is invisible.
 */
export const MARK_VIEW_EXTENT = 1 + MARK_RINGS[0].stroke / 2;

/**
 * Below this rendered size the design doc drops the outer ring and the mark
 * reduces to two shapes — at 24px the outer stroke is a third of a pixel and
 * reads as grey mush.
 */
export const MARK_REDUCE_BELOW_PX = 40;

/** True when a mark at `sizePx` should render the two-shape reduction. */
export function shouldReduce(sizePx) {
  return Number.isFinite(sizePx) && sizePx < MARK_REDUCE_BELOW_PX;
}

/**
 * The rings to draw at a given rendered size.
 *
 * In the reduced form the outer ring drops and the inner takes over as the
 * outline, so the mark keeps its silhouette instead of appearing to shrink.
 */
export function ringsForSize(sizePx) {
  if (!shouldReduce(sizePx)) return MARK_RINGS;
  return MARK_RINGS.filter((ring) => ring.id !== 'outer');
}

/**
 * SVG path for a diamond of half-diagonal `r`, centred on the origin, in a
 * y-down coordinate space. Written as an explicit path rather than a `<rect>`
 * with a transform so the 3D scene and the SVG share one vertex order.
 */
export function diamondPath(r) {
  const v = round(r);
  return `M 0 ${-v} L ${v} 0 L 0 ${v} L ${-v} 0 Z`;
}

/**
 * The four corners of a diamond of half-diagonal `r`, top-first and clockwise
 * in a y-down space. This is what the scene extrudes into a frame.
 */
export function diamondPoints(r) {
  return [
    { x: 0, y: -r },
    { x: r, y: 0 },
    { x: 0, y: r },
    { x: -r, y: 0 },
  ];
}

/** Trim float noise so generated path strings stay stable and diff cleanly. */
function round(n) {
  return Math.round(n * 10000) / 10000;
}

export default MARK_RINGS;
